import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  Drawer,
  Button,
  Form,
  Input,
  Radio,
  notification,
  Select,
  Upload,
  Popconfirm
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useSelector } from 'react-redux';
import validationRules from '../../../assets/validation/rules';
import validateForm from '../../../utils/FormValidation';
import TemplatesAPI from '../../../restAPI/TemplatesAPI';

const Context = React.createContext({
  name: "Default",
});
const { Dragger } = Upload;

const StepForm = forwardRef(({ onRefresh }, ref) => {
  const [visible, setVisible] = useState(false); // Track drawer visibility
  const { token } = useSelector(state => state.auth);
  const [payload, setPayload] = useState({
      "_id": "",
      "file": {},
      "name": "",
      "type": "",
      "status": 1,
      "groupId": "",
      "companyId": ""
    });
    const [errors, setErrors] = useState({});
    const [isModified, setIsModified] = useState(false);
    const [showPopconfirm, setShowPopconfirm] = useState(false);
    const [cancelButtonDisabled, setCancelButtonDisabled] = useState(false);
    const [updating, setUpdating] = useState(false);

  // Expose methods to the parent component
  useImperativeHandle(ref, () => ({
    openEditDrawer: () => {
      setPayload({_id: ref.id});
      getTemplateById(ref.id);
      setVisible(true); // Open drawer
    },
    closeEditDrawer: () => {
      setVisible(false); // Close drawer
      if (onRefresh) onRefresh(); // Trigger refresh on close
    },
  }));

  const getTemplateById = async (id) => {   
      await TemplatesAPI.getTemplateById(token, localStorage.getItem('groupId'), localStorage.getItem('companyId'), id).then((res) => {
          if (res?.data) {
            //console.log(JSON.stringify(res.data));
            let statusValue = 1;
            if(!res.data.status){
              statusValue = 0;
            }
            setPayload({
              "_id": res.data._id,
              "file": res.data.file,
              "name": res.data.name,
              "type": res.data.type,
              "status": statusValue,
              "groupId": res.data.groupId,
              "companyId": res.data.companyId,
            });
          }
      }).catch(err => {
          console.log("error getting template data :", err.message);
      })
    };

   const [api, contextHolder] = notification.useNotification();
    const openNotificationWithIcon = (type, message, description, placement) => {
      api[type]({
        message: message,
        description: description,
        placement
    });
    };

  const contextValue = useMemo(
    () => ({
      name: "Ant Design",
    }),
    []
  );

  const props = {
    name: "file",
    maxCount: 1,
    multiple: false,
    //action: "https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload",
    // onChange(info) {
    //   const { status } = info.file;
    //   if (status !== "uploading") {
    //     //console.log(info.file, info.fileList);
    //   }
    //   if (status === "done") {
    //     message.success(`${info.file.name} file uploaded successfully.`);
    //   } else if (status === "error") {
    //     message.error(`${info.file.name} file upload failed.`);
    //   }
    // },
    beforeUpload: (file) => {
      //console.log(file);
      setPayload({
        "_id": payload._id,
        "file": file,
        "name": payload.name,
        "type": payload.type,
        "status": payload.status,
        "groupId": localStorage.getItem('groupId'),
        "companyId": localStorage.getItem('companyId')
      });
      setErrors({});
      return false;
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };

  const onTemplateTypeChange = (value) => {
    setPayload({
      "_id": payload._id,
      "file": payload.file,
      "name": payload.name,
      "type": value,
      "status": payload.status,
      "groupId": localStorage.getItem('groupId'),
      "companyId": localStorage.getItem('companyId')
    });
    setErrors({});
    setIsModified(true);
  }

  const onRadioChange = (e) => {
    setPayload({
      "_id": payload._id,
      "file": payload.file,
      "name": payload.name,
      "type": payload.type,
      "status": e.target.value,
      "groupId": localStorage.getItem('groupId'),
      "companyId": localStorage.getItem('companyId')
    });
    setIsModified(true);
  };

  function validateFormData(data) {
    const validationResult = validateForm(data, validationRules)
    if (!validationResult.isValid) {
        const err = validationResult.errors
        const newErrors = {};
        Object.keys(err).forEach((value) => {
            newErrors[value] = err[value];
        })
        setErrors(prev => ({ ...prev, ...newErrors }));
    } else {
        const keyData = Object.keys(data)[0]
        setErrors(prev => ({ ...prev, [keyData]: '' }))
    }
}

const onClose = () => {
  setVisible(false);
  if (onRefresh) onRefresh(); // Trigger refresh on drawer close
  setPayload({
    "_id": "",
      "file": {},
      "name": "",
      "type": "",
      "status": 1,
      "groupId": "",
      "companyId": ""
  });
  setErrors({});
  setIsModified(false);
  setUpdating(false);
} 

const onClearChange = () => {
  if (onRefresh) onRefresh(); // Trigger refresh on drawer close
  getTemplateById(payload._id);
  setErrors({});
  setIsModified(false);
  setUpdating(false);
} 

const validateBeforeSubmit = () => {
  const validateData = {
    templateName: payload.name,
    templateType: payload.type,
  }
  const validationResult = validateForm(validateData, validationRules);

  if (!validationResult.isValid) {
      const validationErr = validationResult.errors;
      const newErrors = {};
      Object.keys(validationErr).forEach((value) => {
          newErrors[value] = validationErr[value];
      })
      setErrors(newErrors);
      return false;
  } else {
      return true;
  }
}

const onSubmit = async () => {
  setShowPopconfirm(false);
  setUpdating(true);
  
  let statusValue = true;
  if(payload.status === 0){
    statusValue = false;
  }

  const requestBody = {
    name: payload.name,
    type: payload.type,
    status: statusValue,
    groupId: localStorage.getItem('groupId'),
    companyId: localStorage.getItem('companyId'),
  };

  //console.log(payload.file);

  await TemplatesAPI.updateTemplate(token, localStorage.getItem('groupId'),  localStorage.getItem('companyId'), payload._id, requestBody)
      .then((response) => {
        //console.log(response);
          setTimeout(() => {
            onClose();
            openNotificationWithIcon('success', "Successful", `${response.message}`, 'bottomLeft');
          }, 1000);
      })
      .catch((err) => {
          if (err.response) {
              openNotificationWithIcon('error', 'Template update failed', `${err.response.data.message}`, 'bottomLeft');
          } else {
              openNotificationWithIcon('error', 'Template update failed', `${err.message}`, 'bottomLeft');
          }
      })
      .finally(() => {
        setTimeout(() => {
          setUpdating(false);
        }, 1000);
      });
}

const handleCreateUpdateClick = (e) => {
  e.preventDefault();
  let validation = validateBeforeSubmit();
  if (validation) {
    setShowPopconfirm(true)
  } else {
    setShowPopconfirm(false)
  }
}

const handleCancel = () => {
  setCancelButtonDisabled(true);
  //openNotificationWithIcon('error', 'Abort', `${actionState === "add" ? "Create" : "Update"} cancelled`, 'bottomLeft')
  setTimeout(() => {
      setShowPopconfirm(false);
      setCancelButtonDisabled(false);
  }, 300);
  onClearChange();
  setVisible(false);
};

  return (
    <Context.Provider value={contextValue}>
      {contextHolder}
      <Drawer
        title="Update Template"
        width={700}
        visible={visible}
        onClose={() => onClose()}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button 
              onClick={onClearChange} 
              style={{ marginRight: 8 }} 
              size="large"
              disabled={updating}
            >
              Reset
            </Button>

            <Popconfirm
                title={`Do you want to update the template details?`}
                open={showPopconfirm}
                onConfirm={() => onSubmit()}
                onCancel={handleCancel}
                okText="Yes"
                cancelText="No"
                cancelButtonProps={{ disabled: cancelButtonDisabled }}
              >
              <Button 
                type="primary" 
                size="large" 
                disabled={!isModified || updating} 
                loading={updating}
                onClick={handleCreateUpdateClick}
              >
                {updating ? 'Updating...' : 'Update'}
              </Button> 
              </Popconfirm>
          </div>
        }
      >
        <div style={{ marginTop: 24 }}>
          <Form layout="vertical">
            {/* <Alert message="* Fields are required" type="warning" /> */}

            <Form.Item label="Template Name" validateStatus={errors.templateName ? 'error' : ''} help={errors.templateName || ''} required>
              <Input size="large" name="templateName" value={payload["name"]} onChange={(e) => { setPayload(prev => ({...prev, name: e.target.value})); const validateData = {templateName: e.target.value}; validateFormData(validateData); setIsModified(true);}} placeholder="Template Name" />
            </Form.Item>

            <Form.Item label="Template Type" validateStatus={errors.templateType ? 'error' : ''} help={errors.templateType || ''} required>
              <Select
                showSearch
                name="templateType"
                placeholder="Select Template Type"
                optionFilterProp="label"
                onChange={onTemplateTypeChange}
                size="large"
                value={payload["type"] || "Select a Template Type"}
                //onSearch={onSearch}
                options={[
                  {
                    value: "Accounts receivable",
                    label: "Accounts Receivable",
                  },
                  {
                    value: "Accounts payable",
                    label: "Accounts Payable",
                  },
                ]}
              />
            </Form.Item>

            <Form.Item label="File" hidden validateStatus={errors.templateFile ? 'error' : ''} help={errors.templateFile || ''} required>
              <Dragger {...props}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag Excel file to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for a single file upload.
                </p>
              </Dragger>
            </Form.Item>
            
            <Form.Item label="Status" required>
                <Radio.Group name="status" value={payload["status"]} onChange={onRadioChange}>
                  <Radio value={1}>Active</Radio>
                  <Radio value={0}>Inactive</Radio>
                </Radio.Group>
              </Form.Item>


          </Form>
        </div>
      </Drawer>
    </Context.Provider>
  );
});

export default StepForm;
